import React from "react";
import { ProductStructList } from "../structs/ProductStructList";
import { ProductStruct } from "../structs/ProductStruct";
import Paper from "@mui/material/Paper";
import { BASE_URL_API, PRODUCT_BASE_URL_API } from "../Const";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ListItem from "@mui/material/ListItem";
import Chip from "@mui/material/Chip";
import LoadingSplash from "./commoncComponent/LoadingSplash";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";

/** TTL for localStorage cache: 5 minutes (in ms) */
const PRODUCT_CACHE_TTL = 15 * 60 * 1000; // 300000

export class ProductComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            productList: [],
            loadingVisible: true,
            visibleGroups: 2, // 👈 NEW
        };

        this.loadingRef = React.createRef();
        this.lazyTimer = null; // 👈 NEW
    }

    // 👇 NEW: lazy load groups of categories
    scheduleLazyGroups = () => {
        if (this.lazyTimer) {
            clearTimeout(this.lazyTimer);
        }

        const STEP = 2; // how many groups to add each tick
        const DELAY = 120; // ms between steps

        const loadNext = () => {
            this.setState((prev) => {
                if (
                    !prev.productList ||
                    prev.productList.length === 0 ||
                    prev.visibleGroups >= prev.productList.length
                ) {
                    return null;
                }

                return {
                    visibleGroups: Math.min(
                        prev.visibleGroups + STEP,
                        prev.productList.length
                    ),
                };
            });

            this.lazyTimer = setTimeout(loadNext, DELAY);
        };

        this.lazyTimer = setTimeout(loadNext, DELAY);
    };

    // 👇 NEW: clear timer
    componentWillUnmount() {
        if (this.lazyTimer) {
            clearTimeout(this.lazyTimer);
        }
    }

    componentDidMount() {
        const { market_id, category_id ,scroll_to_top} = this.props;
        const cacheKey = `hk_products_${market_id}_${category_id}`;

        // 🔹 1) Try to load from localStorage cache
        try {
            const cachedStr = localStorage.getItem(cacheKey);
            if (cachedStr) {
                const cached = JSON.parse(cachedStr);

                if (
                    cached &&
                    Array.isArray(cached.data) &&
                    typeof cached.timestamp === "number" &&
                    Date.now() - cached.timestamp < PRODUCT_CACHE_TTL
                ) {
                    // ✅ Valid cache → use it & skip fetch
                    this.setState(
                        {
                            productList: cached.data,
                            loadingVisible: false,
                            visibleGroups: Math.min(
                                2,
                                (cached.data && cached.data.length) || 0
                            ),
                        },
                        () => {
                            this.loadingRef.current?.hide();
                            this.scheduleLazyGroups();
                        }
                    );
                    return;
                } else {
                    // Expired / invalid cache
                    localStorage.removeItem(cacheKey);
                }
            }
        } catch (e) {
            console.warn("Product localStorage cache error:", e);
        }

        // 🔹 2) No valid cache → fetch from API
        this.loadingRef.current?.show("درحال بارگزاری محصولات...");

        let url = BASE_URL_API + "product?market_id=" + market_id;
        if (category_id !== -1) {
            url += "&category_id=" + category_id;
        }

        fetch(url, {
            headers: {
                Authorization: "Bearer " + localStorage.getItem("pwa_token"),
                "Content-Type": "application/json",
            },
        })
            .then((response) => response.json())
            .then((results) => {
                const data = new ProductStructList();

                // گروه‌بندی محصولات بر اساس عنوان دسته (product.category.title)
                results.data.forEach((item) => {
                    if (!item.category) return;

                    let group = data.productList.find(
                        (p) => p.category === item.category.title
                    );

                    if (!group) {
                        group = new ProductStruct();
                        group.category = item.category.title;
                        data.productList.push(group);
                    }

                    group.productList.push(item);
                });

                // مرتب‌سازی محصولات هر گروه: محصولات دارای تصویر اول
                data.productList.forEach((group) => {
                    group.productList.sort((a, b) => {
                        const aHasPic = a.pictures && a.pictures.length > 0;
                        const bHasPic = b.pictures && b.pictures.length > 0;

                        if (aHasPic === bHasPic) return 0;
                        return aHasPic ? -1 : 1;
                    });
                });

                // 🔹 3) Save processed result to localStorage
                try {
                    localStorage.setItem(
                        cacheKey,
                        JSON.stringify({
                            timestamp: Date.now(),
                            data: data.productList,
                        })
                    );
                } catch (e) {
                    // If quota error or something, just log – don't break UI
                    console.warn("Unable to save product cache:", e);
                }

                this.setState(
                    {
                        productList: data.productList,
                        loadingVisible: false,
                        visibleGroups: Math.min(
                            2,
                            (data.productList && data.productList.length) || 0
                        ),
                    },
                    () => {
                        this.loadingRef.current?.hide();
                        this.scheduleLazyGroups();
                    }
                );
            })
            .catch((error) => {
                console.log("error loading products", error);
                this.loadingRef.current?.hide();
                this.setState({ loadingVisible: false });
            });
    }

    scrollToTopIfNeeded = () => {
        const { scroll_to_top } = this.props;

        if (scroll_to_top) {
            try {
                // modern browsers – smooth scroll
                window.scrollTo({ top: 0, behavior: "smooth" });
            } catch (e) {
                // fallback for older ones
                window.scrollTo(0, 0);
            }
        }
    };


    renderProductCard = (product) => {
        const MAX_LENGTH = 20;

        const hasVariants =
            Array.isArray(product.variants) &&
            product.variants.length > 0;

        const formatStep = (val) => {
            const num = Number(val);
            if (!isFinite(num) || num <= 0) return "";
            return parseFloat(num.toFixed(4)).toLocaleString("en", {
                maximumFractionDigits: 4,
            });
        };

        const formatTomans = (val) =>
            Number(val || 0)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + " تومان ";

        const formatNumber = (val) =>
            Number(val || 0)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        // -------- INVENTORY --------
        const totalStock = hasVariants
            ? product.variants.reduce(
                (sum, v) => sum + Number(v.step || 0),
                0
            )
            : Number(product.step || 0);

        // -------- ATTRIBUTE GROUPING --------
        let attributeSummaryText = "";
        let attributeValueChips = [];

        if (hasVariants) {
            const attributeMap = {};

            product.variants.forEach(v => {
                (v.attribute_values || []).forEach(av => {
                    const title = av?.attribute?.title || "مدل";
                    const value = av?.value;

                    if (!attributeMap[title]) {
                        attributeMap[title] = new Set();
                    }

                    attributeMap[title].add(value);
                });
            });

            // Build summary text
            const summaryParts = Object.entries(attributeMap).map(
                ([title, values]) =>
                    `${values.size} ${title}`
            );

            attributeSummaryText = summaryParts.join(" و ");

            // Build chips
            attributeValueChips = Object.values(attributeMap)
                .flatMap(set => Array.from(set))
                .slice(0, 4);
        }

        // -------- VARIANT PRICE --------
        let variantPriceBlock = null;

        if (hasVariants) {
            const variantData = product.variants
                .map(v => {
                    const base = Number(v.customer_price || 0);
                    const percent = Number(v.seller_discount || 0);
                    if (base <= 0) return null;

                    const final =
                        percent > 0
                            ? base - (base * percent / 100)
                            : base;

                    return { base, percent, final };
                })
                .filter(Boolean);

            if (variantData.length > 0) {
                const bases = variantData.map(v => v.base);
                const finals = variantData.map(v => v.final);

                const minBase = Math.min(...bases);
                const maxBase = Math.max(...bases);
                const minFinal = Math.min(...finals);
                const maxFinal = Math.max(...finals);

                const hasDiscount = variantData.some(
                    v => v.percent > 0
                );

                variantPriceBlock = (
                    <Box sx={{ mt: "auto" }}>
                        {hasDiscount && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    textDecoration: "line-through",
                                    direction: "rtl",
                                    fontSize: 12
                                }}
                            >
                                {minBase === maxBase
                                    ? formatTomans(minBase)
                                    : `${formatNumber(minBase)} تا ${formatTomans(maxBase)}`
                                }
                            </Typography>
                        )}

                        <Typography
                            variant="body1"
                            sx={{
                                color: "success.main",
                                fontWeight: 700,
                                direction: "rtl",
                            }}
                        >
                            {minFinal === maxFinal
                                ? formatTomans(Math.round(minFinal))
                                : `${formatNumber(Math.round(minFinal))} تا ${formatTomans(Math.round(maxFinal))}`
                            }
                        </Typography>
                    </Box>
                );
            }
        }

        return (
            <ListItem
                key={product.id}
                component={Link}
                to={"/product?id=" + product.id}
                onClick={this.scrollToTopIfNeeded}
                sx={{
                    width: 250,
                    px: 0,
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                <Paper
                    elevation={1}
                    sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: 2,
                        p: 1.25,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    {/* IMAGE */}
                    <Box
                        sx={{
                            width: 140,
                            height: 140,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            bgcolor: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            alignSelf: "center",
                        }}
                    >
                        <Box
                            component="img"
                            alt={product.title}
                            src={
                                product.pictures?.length > 0
                                    ? PRODUCT_BASE_URL_API +
                                    product.pictures[0].url
                                    : "hamekara-logo.png"
                            }
                            sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                            }}
                        />
                    </Box>

                    {/* TITLE */}
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            direction: "rtl",
                        }}
                    >
                        {product.title.length > MAX_LENGTH
                            ? `${product.title.substring(0, MAX_LENGTH)}...`
                            : product.title}
                    </Typography>

                    {/* ATTRIBUTE SUMMARY BADGE */}
                    {hasVariants && (
                        <Chip
                            label={attributeSummaryText}
                            size="small"
                            sx={{
                                fontSize: 11,
                                height: 22,
                                alignSelf: "flex-start",
                                bgcolor: "#d32f2f",
                                color: "#fff",
                                fontWeight: 600,
                            }}
                        />
                    )}

                    {/* ATTRIBUTE VALUE CHIPS */}
                    {attributeValueChips.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                direction: "rtl",
                            }}
                        >
                            {attributeValueChips.map((val, index) => (
                                <Chip
                                    key={index}
                                    label={val}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        fontSize: 10,
                                        height: 20,
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    {/* INVENTORY */}
                    <Typography
                        variant="caption"
                        sx={{
                            color:
                                totalStock > 0
                                    ? "#909090"
                                    : "error.main",
                            direction: "rtl",
                        }}
                    >
                        {totalStock > 0
                            ? `موجودی: ${formatStep(totalStock)}`
                            : "تماس بگیرید"}
                    </Typography>

                    {/* PRICE */}
                    {hasVariants
                        ? variantPriceBlock
                        : Number(product.del_price) > 0 ? (
                            <Box sx={{ mt: "auto" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textDecoration: "line-through",
                                        direction: "rtl",
                                    }}
                                >
                                    {formatTomans(product.price)}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: "success.main",
                                        fontWeight: 700,
                                        direction: "rtl",
                                    }}
                                >
                                    {formatTomans(
                                        parseInt(product.price || 0, 10) -
                                        parseInt(product.del_price || 0, 10)
                                    )}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography
                                variant="body1"
                                sx={{
                                    color:
                                        Number(product.price) > 0
                                            ? "success.main"
                                            : "warning.main",
                                    fontWeight: 700,
                                    direction: "rtl",
                                    mt: "auto",
                                }}
                            >
                                {Number(product.price) > 0
                                    ? formatTomans(product.price)
                                    : "تماس بگیرید."}
                            </Typography>
                        )}
                </Paper>
            </ListItem>
        );
    };

    render() {
        const { loadingVisible, productList, visibleGroups } = this.state;

        const containerStyle = {
            display: "flex",
            flexDirection: "row",
            overflowX: "auto",
            overflowY: "hidden",
            padding: "16px 8px",
            direction: "rtl",
            gap: 12,
        };

        return (
            <>
                {/* Splash overlay */}
                <LoadingSplash ref={this.loadingRef} />

                {/* Main content */}
                {!loadingVisible &&
                productList
                    .slice(0, visibleGroups) // 👈 NEW: lazy groups
                    .map((group, i) => {
                        const categoryTitle =
                            group?.category || "دسته‌بندی";
                        const firstProduct = group?.productList?.[0];
                        const categoryId = firstProduct?.category?.id;

                        const hrefAll = categoryId
                            ? `/product-list/?market_id=${this.props.market_id}&category_id=${categoryId}`
                            : "#";

                        return (
                            <Box key={i} sx={{ mb: 4, mx: 0.5 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        position: "relative",
                                        direction: "rtl",
                                        borderRadius: 3,
                                        border: "0px solid",
                                        borderColor: "divider",
                                        backgroundColor: "#fafafa",
                                        boxShadow:
                                            "0 6px 16px rgba(0,0,0,0.06)",
                                        p: 0,
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Header / category label + "more" icon */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "space-between",
                                            direction: "rtl",
                                            position: "relative",
                                            px: 0,
                                            pt: 0,
                                            pb: 0,
                                        }}
                                    >
                                        {/* Category ribbon */}
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                px: 1.5,
                                                height: 34,
                                                bgcolor: "#FF1744",
                                                color: "#fff",
                                                fontSize: 14,
                                                fontWeight: 800,
                                                borderRadius:
                                                    "0 0 14px 14px",
                                                boxShadow:
                                                    "0 2px 6px rgba(0,0,0,0.12)",
                                                lineHeight: 1,
                                                userSelect: "none",
                                                maxWidth: "75%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                            title={categoryTitle}
                                        >
                                            {categoryTitle}
                                        </Box>

                                        {/* "More" button */}
                                        {categoryId && (
                                            <Tooltip
                                                title="مشاهده همه محصولات"
                                                arrow
                                                placement="top"
                                            >
                                                <IconButton
                                                    href={hrefAll}
                                                    sx={{
                                                        color: "#9a1111",
                                                        transition:
                                                            "all .2s ease",
                                                        border:
                                                            "1px solid rgba(154,17,17,0.25)",
                                                        backgroundColor:
                                                            "#fff",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "#9a1111",
                                                            color: "#fff",
                                                            boxShadow:
                                                                "0 3px 8px rgba(154,17,17,0.3)",
                                                        },
                                                        width: 34,
                                                        height: 34,
                                                        mt: 1.5,
                                                        ml: 1.5,
                                                    }}
                                                >
                                                    <ArrowForwardIosRoundedIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            transform:
                                                                "scaleX(-1)",
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>

                                    {/* Horizontal product list (max 20 items) */}
                                    <div
                                        style={{
                                            ...containerStyle,
                                            marginTop: 0,
                                        }}
                                    >
                                        {group.productList
                                            .slice(0, 20)
                                            .map((product) =>
                                                this.renderProductCard(
                                                    product
                                                )
                                            )}
                                    </div>
                                </Paper>
                            </Box>
                        );
                    })}
            </>
        );
    }
}
